import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { analyzeReport, type ReportAnalysis } from "@/lib/analyze-report.functions";
import { findNearbyDuplicates, SEVERITY_META, type NearbyDuplicate } from "@/lib/reports";
import { CATEGORIES, CATEGORY_LIST, type Category } from "@/lib/categories";
import { toast } from "sonner";
import {
  Camera, ImagePlus, Loader2, MapPin, ClipboardList, X, ShieldAlert,
  CheckCircle2, AlertTriangle, ArrowRight, Building2, Users, Video, Film,
} from "lucide-react";
import { useMediaUploads } from "@/lib/use-media-uploads";
import { MediaAttachments } from "@/components/media-attachments";
import { VIDEO_ACCEPT, MEDIA_LIMITS, formatDuration } from "@/lib/media";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/report")({
  head: () => ({
    meta: [
      { title: "Xamseen — Report a Problem" },
      { name: "description", content: "Photograph a community problem, we analyse it, pin it on the map and track it to resolution." },
      { property: "og:title", content: "Xamseen — Report a Problem" },
      { property: "og:description", content: "See it. Report it. Track it until it's fixed." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: ReportPage,
});

type Step = "capture" | "analyzing" | "review";
type Shot = { url: string; file: string };

function ReportPage() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const recordVideoRef = useRef<HTMLInputElement>(null);
  const pickVideoRef = useRef<HTMLInputElement>(null);
  const clips = useMediaUploads("reports", { maxItems: 2 });

  const [step, setStep] = useState<Step>("capture");
  const [shots, setShots] = useState<Shot[]>([]);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState("");
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [locationName, setLocationName] = useState("");
  const [locating, setLocating] = useState(false);
  const [analysis, setAnalysis] = useState<ReportAnalysis | null>(null);
  const [category, setCategory] = useState<Category>("community");
  const [duplicates, setDuplicates] = useState<NearbyDuplicate[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (profile?.city || profile?.country) {
      setLocationName([profile.neighborhood, profile.city, profile.country].filter(Boolean).join(", "));
    }
    detectLocation(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.city]);

  async function detectLocation(silent = false) {
    if (!navigator.geolocation) { if (!silent) toast.error("Location not supported on this device"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude, lng = pos.coords.longitude;
        setCoords({ lat, lng });
        try {
          const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=16&addressdetails=1`, { headers: { "Accept-Language": "en" } });
          const j = await r.json();
          const a = j?.address ?? {};
          const friendly = [
            a.road || a.pedestrian,
            a.suburb || a.neighbourhood || a.city_district,
            a.city || a.town || a.village || a.municipality,
            a.country,
          ].filter(Boolean).join(", ");
          setLocationName(friendly || `${lat.toFixed(4)}, ${lng.toFixed(4)}`);
        } catch {
          setLocationName(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
        }
        setLocating(false);
      },
      () => { setLocating(false); if (!silent) toast.error("Couldn't get your location"); },
      { enableHighAccuracy: true, timeout: 9000, maximumAge: 60000 }
    );
  }

  async function addPhotos(files: FileList | null) {
    if (!files || !user) return;
    setUploading(true);
    const next: Shot[] = [];
    for (const f of Array.from(files).slice(0, 4)) {
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name} is too large (max 8MB)`); continue; }
      const ext = f.name.split(".").pop() ?? "jpg";
      const path = `${user.id}/reports/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error } = await supabase.storage.from("post-media").upload(path, f, { upsert: false, contentType: f.type });
      if (error) { toast.error(error.message); continue; }
      const { data: pub } = supabase.storage.from("post-media").getPublicUrl(path);
      next.push({ url: pub.publicUrl, file: f.name });
    }
    setShots((p) => [...p, ...next].slice(0, 4));
    setUploading(false);
  }

  async function runAnalysis() {
    if (shots.length === 0) { toast.error("Add at least one photo of the problem"); return; }
    setStep("analyzing");
    try {
      const result = await analyzeReport({
        data: { imageUrls: shots.map((s) => s.url), description: note.trim() || null, locationName: locationName || null },
      });
      setAnalysis(result);
      setCategory(result.category);
      if (coords) {
        const dups = await findNearbyDuplicates({ lat: coords.lat, lng: coords.lng, category: result.category });
        setDuplicates(dups);
      }
      setStep("review");
    } catch (e: any) {
      toast.error(e?.message ?? "Analysis failed — you can still submit the report");
      setAnalysis(null);
      setStep("review");
    }
  }

  async function submit() {
    if (!user || !analysis) return;
    setSubmitting(true);
    const title = analysis.title || analysis.problem_type;
    const severity = analysis.severity;

    const { data, error } = await supabase
      .from("posts")
      .insert({
        author_id: user.id,
        title,
        content: note.trim() || analysis.summary || title,
        category: category as any,
        post_type: severity === "critical" ? ("emergency" as any) : ("issue" as any),
        status: "open" as any,
        severity: severity as any,
        latitude: coords?.lat ?? null,
        longitude: coords?.lng ?? null,
        location_name: locationName.trim() || null,
        image_url: shots[0]?.url ?? null,
        tags: [analysis.problem_type.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")].filter(Boolean),
      })
      .select("id")
      .single();

    if (error || !data) { setSubmitting(false); toast.error(error?.message ?? "Could not submit report"); return; }

    const mediaRows = [
      ...shots.map((s, i) => ({
        post_id: data.id, url: s.url, media_type: "image", position: i,
        poster_url: null, duration_seconds: null, byte_size: null,
        storage_bucket: null, storage_path: null, poster_path: null,
      })),
      ...clips.items.map((m, i) => ({
        post_id: data.id,
        url: m.url,
        media_type: m.kind,
        position: shots.length + i,
        poster_url: m.posterUrl ?? null,
        duration_seconds: m.durationSeconds ?? null,
        byte_size: m.byteSize ?? null,
        storage_bucket: m.storageBucket ?? null,
        storage_path: m.storagePath ?? null,
        poster_path: m.posterPath ?? null,
      })),
    ];
    if (mediaRows.length) {
      await supabase.from("post_media").insert(mediaRows);
    }

    await supabase.from("report_analyses").insert({
      post_id: data.id,
      problem_type: analysis.problem_type,
      category: category as any,
      severity: severity as any,
      confidence: analysis.confidence,
      summary: analysis.summary,
      potential_risks: analysis.potential_risks,
      recommended_action: analysis.recommended_action,
      responsible_authority: analysis.responsible_authority,
      model: analysis.model,
      is_fallback: analysis.is_fallback,
    });

    if (severity === "critical" || severity === "urgent") {
      await supabase.from("emergency_alerts").insert({
        created_by: user.id,
        post_id: data.id,
        title,
        body: analysis.summary || title,
        severity: severity as any,
        latitude: coords?.lat ?? null,
        longitude: coords?.lng ?? null,
        location_name: locationName.trim() || null,
      });
    }

    setSubmitting(false);
    toast.success("Report submitted — tracking started");
    navigate({ to: "/posts/$id", params: { id: data.id } });
  }

  if (!user) {
    return (
      <AppShell>
        <Card className="mx-auto max-w-md p-8 text-center">
          <ShieldAlert className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <h1 className="font-display text-xl font-semibold">Sign in to report a problem</h1>
          <p className="mt-1 text-sm text-muted-foreground">Reports are tied to your civic profile so the community can verify and track them.</p>
          <Button asChild className="mt-4 w-full"><Link to="/auth">Sign in</Link></Button>
        </Card>
      </AppShell>
    );
  }

  const sev = analysis ? SEVERITY_META[analysis.severity] : null;

  return (
    <AppShell>
      <div className="mx-auto max-w-xl space-y-5 pb-10">
        <header>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Report a Problem</h1>
          <p className="text-sm text-muted-foreground">See it → report it → understand it → the community verifies → it gets tracked to resolution.</p>
        </header>

        <ol className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          {(["Photo", "Analysis", "Confirm"] as const).map((label, i) => {
            const activeIndex = step === "capture" ? 0 : step === "analyzing" ? 1 : 2;
            return (
              <li key={label} className="flex items-center gap-2">
                <span className={cn("grid h-6 w-6 place-items-center rounded-full border text-[11px]",
                  i <= activeIndex ? "border-primary bg-primary text-primary-foreground" : "border-border")}>{i + 1}</span>
                <span className={cn(i <= activeIndex && "text-foreground")}>{label}</span>
                {i < 2 && <ArrowRight className="h-3 w-3" />}
              </li>
            );
          })}
        </ol>

        {step !== "review" && (
          <Card className="space-y-4 p-4">
            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-medium">Photos of the problem</p>
                <span className="text-xs text-muted-foreground">{shots.length}/4</span>
              </div>

              {shots.length > 0 && (
                <div className="mb-3 grid grid-cols-3 gap-2">
                  {shots.map((s) => (
                    <div key={s.url} className="relative aspect-square overflow-hidden rounded-xl border border-border">
                      <img src={s.url} alt="Reported problem" className="h-full w-full object-cover" loading="lazy" />
                      <button onClick={() => setShots((p) => p.filter((x) => x.url !== s.url))}
                        className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-background/90 text-foreground">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant="outline" className="h-20 flex-col gap-1" disabled={uploading || shots.length >= 4}
                  onClick={() => cameraRef.current?.click()}>
                  <Camera className="h-5 w-5" /><span className="text-xs">Take photo</span>
                </Button>
                <Button type="button" variant="outline" className="h-20 flex-col gap-1" disabled={uploading || shots.length >= 4}
                  onClick={() => galleryRef.current?.click()}>
                  <ImagePlus className="h-5 w-5" /><span className="text-xs">From gallery</span>
                </Button>
              </div>
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => addPhotos(e.target.files)} />
              <input ref={galleryRef} type="file" accept="image/*" multiple hidden onChange={(e) => addPhotos(e.target.files)} />
              {uploading && <p className="mt-2 flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" />Uploading…</p>}
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-medium">Video of the problem <span className="font-normal text-muted-foreground">(optional)</span></p>
                <span className="text-xs text-muted-foreground">{clips.items.length}/2</span>
              </div>
              <MediaAttachments items={clips.items} tasks={clips.tasks} onRemove={clips.remove} onMove={clips.move} className="mb-3" />
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant="outline" className="h-20 flex-col gap-1"
                  disabled={clips.busy || clips.items.length >= 2}
                  onClick={() => recordVideoRef.current?.click()}>
                  <Video className="h-5 w-5" /><span className="text-xs">Record video</span>
                </Button>
                <Button type="button" variant="outline" className="h-20 flex-col gap-1"
                  disabled={clips.busy || clips.items.length >= 2}
                  onClick={() => pickVideoRef.current?.click()}>
                  <Film className="h-5 w-5" /><span className="text-xs">From gallery</span>
                </Button>
              </div>
              <input ref={recordVideoRef} type="file" accept="video/*" capture="environment" hidden
                onChange={(e) => { if (user) void clips.addFiles(Array.from(e.target.files ?? []), user.id); e.target.value = ""; }} />
              <input ref={pickVideoRef} type="file" accept={VIDEO_ACCEPT} hidden
                onChange={(e) => { if (user) void clips.addFiles(Array.from(e.target.files ?? []), user.id); e.target.value = ""; }} />
              <p className="mt-2 text-xs text-muted-foreground">
                Up to {MEDIA_LIMITS.video.label} and {formatDuration(MEDIA_LIMITS.video.maxSeconds)} per clip. Video is stored privately and
                only played back through a short-lived link.
              </p>
            </div>

            <div>
              <p className="mb-1.5 text-sm font-medium">Anything to add? <span className="font-normal text-muted-foreground">(optional)</span></p>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                placeholder="e.g. This drain has been blocked for two weeks and floods the road when it rains." />
            </div>

            <div>
              <p className="mb-1.5 text-sm font-medium">Location</p>
              <div className="flex gap-2">
                <Input value={locationName} onChange={(e) => setLocationName(e.target.value)} placeholder="Street, area, city" />
                <Button type="button" variant="outline" size="icon" onClick={() => detectLocation()} disabled={locating} aria-label="Use my location">
                  {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
                </Button>
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {coords ? `Pinned at ${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)} — it will appear on the civic map.` : "Add GPS so this report shows on the map and matches nearby reports."}
              </p>
            </div>

            <Button className="h-11 w-full" onClick={runAnalysis} disabled={step === "analyzing" || uploading || clips.busy || shots.length === 0}>
              {step === "analyzing" ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Analysing…</> : <><ClipboardList className="mr-2 h-4 w-4" />Analyse this report</>}
            </Button>
          </Card>
        )}

        {step === "analyzing" && (
          <Card className="space-y-3 p-6 text-center">
            <Loader2 className="mx-auto h-7 w-7 animate-spin text-primary" />
            <p className="font-medium">Reviewing your photos</p>
            <p className="text-sm text-muted-foreground">Identifying the problem, estimating severity and finding who is responsible.</p>
          </Card>
        )}

        {step === "review" && analysis && (
          <div className="space-y-4">
            <Card className="space-y-4 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <ClipboardList className="h-3.5 w-3.5 text-primary" /> Assessment
                  </p>
                  <h2 className="mt-1 font-display text-lg font-semibold leading-snug">{analysis.problem_type}</h2>
                </div>
                {sev && <Badge variant="outline" className={cn("shrink-0", sev.badge)}>{sev.label} severity</Badge>}
              </div>

              {analysis.summary && <p className="text-sm text-muted-foreground">{analysis.summary}</p>}

              {analysis.is_fallback && (
                <p className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-2.5 text-xs text-muted-foreground">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5" />
                  Automatic analysis wasn't available. Pick the right category below and a moderator will review.
                </p>
              )}

              {analysis.potential_risks.length > 0 && (
                <div>
                  <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Potential risks</p>
                  <div className="flex flex-wrap gap-1.5">
                    {analysis.potential_risks.map((r) => (
                      <Badge key={r} variant="secondary" className="font-normal">{r}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {analysis.recommended_action && (
                <div className="rounded-xl border border-border p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Recommended action</p>
                  <p className="mt-1 text-sm">{analysis.recommended_action}</p>
                </div>
              )}

              <div className="flex items-center gap-2 text-sm">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Likely responsible:</span>
                <span className="font-medium">{analysis.responsible_authority}</span>
              </div>

              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Category {analysis.confidence > 0 && <span className="normal-case text-muted-foreground">· {Math.round(analysis.confidence * 100)}% confidence</span>}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {CATEGORY_LIST.map((c) => (
                    <button key={c.key} onClick={() => setCategory(c.key)}
                      className={cn("flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                        category === c.key ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground")}>
                      <c.icon className="h-3.5 w-3.5" style={{ color: category === c.key ? "currentColor" : c.color }} />
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            </Card>

            {duplicates.length > 0 && (
              <Card className="space-y-3 p-4">
                <p className="flex items-center gap-2 text-sm font-medium"><Users className="h-4 w-4 text-primary" />Someone may have reported this already</p>
                <p className="text-xs text-muted-foreground">Confirming an existing report is stronger than filing a duplicate.</p>
                <div className="space-y-2">
                  {duplicates.map((d) => (
                    <Link key={d.id} to="/posts/$id" params={{ id: d.id }}
                      className="block rounded-xl border border-border p-3 transition-colors hover:border-primary/40">
                      <p className="text-sm font-medium leading-snug">{d.title}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {d.distance_km < 0.1 ? "<100 m away" : `${(d.distance_km * 1000).toFixed(0)} m away`} · {CATEGORIES[d.category as Category]?.label} · {d.verify_count} confirmations
                      </p>
                    </Link>
                  ))}
                </div>
              </Card>
            )}

            <Card className="space-y-2 border-ember/35 bg-ember/[0.05] p-4">
              <p className="text-sm font-medium">Before you submit</p>
              <ul className="space-y-1.5 pl-4 text-xs leading-relaxed text-foreground/80">
                <li className="list-disc">
                  This report becomes <strong>publicly visible</strong> on Xamseen, along with your username and the
                  photos you attached.
                </li>
                <li className="list-disc">
                  Check your photos for faces, licence plates, house numbers and documents. Do not include people's
                  private information.
                </li>
                <li className="list-disc">
                  Location is stored at your chosen precision (
                  <Link to="/settings" className="font-medium text-plum hover:underline">
                    change it in Settings
                  </Link>
                  ). Describe the problem, not a person.
                </li>
                <li className="list-disc">
                  Xamseen is not an emergency service — for immediate danger, call your local emergency number.
                </li>
              </ul>
              <p className="pt-1 text-xs text-muted-foreground">
                See{" "}
                <Link to="/community-guidelines" className="hover:underline">
                  Community Guidelines
                </Link>{" "}
                and{" "}
                <Link to="/data-use" className="hover:underline">
                  how Xamseen uses data
                </Link>
                .
              </p>
            </Card>

            <Card className="space-y-3 p-4">
              <p className="text-xs text-muted-foreground">
                Submitting pins this report on the civic map, opens it for community confirmation and starts status tracking.
              </p>

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setStep("capture")} disabled={submitting}>Back</Button>
                <Button className="flex-1 gap-2" onClick={submit} disabled={submitting}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Submit report
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </AppShell>
  );
}
